import { useMediaQuery } from '@material-ui/core';
import { Link, SolvTheme, Stack, Text } from '@solvhealth/jigsaw-v1';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  ACCOUNT_HOME_REBOOKING_SECTION,
  CARE_TEAM_BOOK_AGAIN_SEE_ALL,
} from '~/core/analytics/events';
import { LocationWithCareTypes, useCareTeam } from '~/components/CareTeam/hooks/useCareTeam';
import LocationBookAgainCard from './LocationBookAgainCard';
import ProviderBookAgainCard from '~/components/AccountGetsMarried/components/GetCare/components/BookAgain/ProviderBookAgainCard';
import { ProviderWithCareTypes } from '~/core/dapi/careTeam';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import LocationBookAgainCardWithSlots from '~/components/AccountGetsMarried/components/GetCare/components/BookAgain/LocationBookAgainCardWithSlots';

// Make sure a little bit of the next card shows as an affordance for scroll behavior
const ConstrainedBookAgainCard = css`
  --scroll-affordance: 30px;
  --x-padding: ${SolvTheme.space[3]};
  max-width: min(calc(100vw - var(--x-padding) - var(--gap) - var(--scroll-affordance)), 375px);
  &:last-of-type {
    --scroll-affordance: 0px;
  }
`;

const StyledLocationBookAgainCard = styled(LocationBookAgainCard).withConfig({ componentId: 'sc-9kwo4p-0' })`
  ${ConstrainedBookAgainCard}
`;

const StyledLocationBookAgainCardWithSlots = styled(LocationBookAgainCardWithSlots).withConfig({ componentId: 'sc-9kwo4p-1' })`
  ${ConstrainedBookAgainCard}
`;

const StyledProviderBookAgainCard = styled(ProviderBookAgainCard).withConfig({ componentId: 'sc-9kwo4p-2' })`
  ${ConstrainedBookAgainCard};
  min-width: 320px;
`;

/**
 * This is a module displaying the three re-booking suggestions
 * for the user based on their previous booking locations (and
 * associated locations)
 */
const BookAgain = () => {
  const { allCareTypes } = useCareTeam();
  const flags = useLDFlags();

  const { t } = useTranslation(['account']);

  useEffect(() => {
    analyticsTrackEvent(ACCOUNT_HOME_REBOOKING_SECTION, {
      hasRebookingOptions: !isEmpty(allCareTypes),
      numRebookingOptions: allCareTypes.length,
    });
  }, [allCareTypes]);

  const handleSeeAllOnClick = () => {
    analyticsTrackEvent(CARE_TEAM_BOOK_AGAIN_SEE_ALL);
  };

  if (isEmpty(allCareTypes)) return null;

  const topThreeCareTeamsMembers = allCareTypes.slice(0, 3);

  const isProviderCareTeamMember = (
    careTeam: LocationWithCareTypes | ProviderWithCareTypes
  ): careTeam is ProviderWithCareTypes => {
    return (careTeam as ProviderWithCareTypes).careTypes.includes('provider');
  };

  return (
    <Stack space={1}>
      <Stack alignItems="baseline" justifyContent="space-between" px={3} row>
        <Text variant="h3">{t('account:home.bookAgain.title')}</Text>
        <Link href="/account/care-team" onClick={handleSeeAllOnClick}>
          {t('account:home.bookAgain.seeAll')}
        </Link>
      </Stack>

      {/* Use a 3-wide grid display on desktop, but flex container on mobile to allow scrolling */}
      <Stack
        alignItems="stretch"
        display="flex"
        overflowX="scroll"
        pb={1}
        pt={1}
        px={3}
        row
        space={2.5}
      >
        {topThreeCareTeamsMembers.map((careTeamMember) => {
          if (isProviderCareTeamMember(careTeamMember)) {
            return (
              <StyledProviderBookAgainCard key={careTeamMember.id} provider={careTeamMember} />
            );
          }
          if (flags.mary259ShowSlotsTodayInBookAgain) {
            return (
              <StyledLocationBookAgainCardWithSlots
                key={careTeamMember.id}
                location={careTeamMember}
              />
            );
          }
          return <StyledLocationBookAgainCard key={careTeamMember.id} location={careTeamMember} />;
        })}
      </Stack>
    </Stack>
  );
};

export default BookAgain;
